import React from 'react';
import { Column } from '@/types/common';
import {
  COLLECTION_PURPOSE_LABEL,
  CollectionPurpose,
  PaidCollectionItem,
} from '@/types/rental';
import { formatRupiah, formatWibDateTime } from '@/utils/format';
import { collectionVoucherLabel, formatVehicleLabel } from './rentalDisplay';

/**
 * The settled-payment table columns, shared by the dashboard's "Pembayaran Hari
 * Ini" tab and the standalone Pembayaran menu.
 *
 * Lives beside `rentalDisplay` for the same reason that module does: it is rent
 * display logic over a rent type, reused across rent screens, and a second
 * hand-typed copy of the Mitra/Unit/Transaksi cells is how the two screens
 * start disagreeing about what a payment looks like.
 *
 * Time is the one column the two screens genuinely differ on — the tab sits
 * under a "today" header, the menu spans days — so it ships as two named
 * variants rather than a fork.
 */
export const mitraColumn: Column<PaidCollectionItem> = {
  key: 'driver',
  title: 'Mitra',
  render: (_value: unknown, record: PaidCollectionItem) => (
    <div className="leading-tight">
      <div className="text-[14px] text-gray-900">
        {record.driver?.name || '-'}
      </div>
      <div className="text-[12px] text-gray-500">
        {record.driver?.code || '-'}
      </div>
    </div>
  ),
};

export const unitColumn: Column<PaidCollectionItem> = {
  key: 'vehicle',
  title: 'Unit',
  render: (_value: unknown, record: PaidCollectionItem) =>
    record.vehicle ? (
      <div className="leading-tight">
        <div className="text-[14px] text-gray-900">
          {record.vehicle.platNomor || '-'}
        </div>
        <div className="text-[12px] text-gray-500">
          {formatVehicleLabel(record.vehicle) || '-'}
        </div>
      </div>
    ) : (
      // null until a unit is assigned — a PRE_ORDER's DP is paid before that.
      <span className="text-[12px] text-gray-400">Belum ditugaskan</span>
    ),
};

export const transactionColumn: Column<PaidCollectionItem> = {
  key: 'code',
  title: 'Transaksi',
  render: (_value: unknown, record: PaidCollectionItem) => (
    <div className="leading-tight">
      <div className="text-[14px] text-gray-900">{record.code || '-'}</div>
      <div className="text-[12px] text-gray-500">
        {record.rental?.code || '-'}
      </div>
    </div>
  ),
};

export const methodColumn: Column<PaidCollectionItem> = {
  key: 'method',
  title: 'Metode',
  render: (_value: unknown, record: PaidCollectionItem) => (
    <div className="leading-tight">
      <div className="text-[14px] text-gray-900">{record.method || '-'}</div>
      <div className="text-[12px] text-gray-500">
        {COLLECTION_PURPOSE_LABEL[record.purpose as CollectionPurpose] ||
          record.purpose ||
          '-'}
      </div>
    </div>
  ),
};

export const amountColumn: Column<PaidCollectionItem> = {
  key: 'amount',
  title: 'Jumlah',
  render: (_value: unknown, record: PaidCollectionItem) => (
    <div className="leading-tight">
      <div className="text-[14px] font-medium text-gray-900">
        {formatRupiah(record.amount, '-', { space: false })}
      </div>
      {record.adminFeeCharged ? (
        <div className="text-[12px] text-gray-500">
          + {formatRupiah(record.adminFeeCharged, '-', { space: false })} admin
        </div>
      ) : null}
      {collectionVoucherLabel(record) ? (
        <div className="text-[12px] text-gray-500">
          {collectionVoucherLabel(record)}
        </div>
      ) : null}
    </div>
  ),
};

/**
 * Two variants of the same column, because the screens ask different questions
 * of the field: rows under a "today" header need the hour alone, a list that
 * spans days needs the date with it.
 */
const paidAtColumn = (
  title: string,
  withDate: boolean
): Column<PaidCollectionItem> => ({
  key: 'paidAt',
  title,
  render: (_value: unknown, record: PaidCollectionItem) => (
    <span className="text-[14px] text-gray-900">
      {formatWibDateTime(record.paidAt, { withDate })}
    </span>
  ),
});

export const paidTimeColumn = paidAtColumn('Jam Bayar', false);
export const paidDateTimeColumn = paidAtColumn('Waktu Bayar', true);
